import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosError, AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { Product, ProductType } from "../../models/Product";
import { Partner } from "../../models/Partner";
import { ProcessingRequestDetails } from "../../models/ProcessingRequest";
import {
  ProcessingLogConsumption,
  ProcessingLogConsumptionCreate,
} from "../../models/ProcessingLog";
import { ProcessingOrderDetails } from "../../models/ProcessingOrder";
import { ProcessingLogDetails } from "../../models/ProcessingLog";
import { Utility } from "../../core/Utility";
import { getProcessingLogTemplate } from "../../document-templates/ProcessingLogTemplate";
import {
  getOneProcessingLog,
  addNewProcessingLog,
  updateOneProcessingLog,
  getProcessingOrders,
  getOperatorOrderConsumptions,
} from "../../services/FinishingCenterService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import "./ProcessingLogOverviewPage.scss";

export interface ProcessingLogPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const ProcessingLogOverviewPage = (p: ProcessingLogPageProps) => {
  const { buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [updating, setUpdating] = useState<boolean>(false);
  const [loadingLookups, setLoadingLookups] = useState<boolean>(true);
  const [newProcessingLog, setNewProcessingLog] =
    useState<ProcessingLogDetails>({ closed: false } as ProcessingLogDetails);
  const [orders, setOrders] = useState<ProcessingOrderDetails[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [currentConsumption, setCurrentConsumption] = useState<
    ProcessingLogConsumptionCreate & {
      id?: string;
      productName?: string;
      partnerName?: string;
    }
  >({
    productId: "",
    uom: "",
    amount: 0,
    isForeign: false,
    partnerId: undefined,
  });
  const [editingConsumptionIndex, setEditingConsumptionIndex] = useState<
    number | null
  >(null);

  const selectedOrder = orders.find(
    (o) => o.id === (newProcessingLog.orderId ?? newProcessingLog.order?.id),
  );
  const [pdfData, setPdfData] = useState<any>();
  getProcessingLogTemplate(
    newProcessingLog,
    selectedOrder as ProcessingOrderDetails,
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    const status = resp?.data?.status;
    if (status === "ERROR" || status === "error") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const loadLookupsAndOrders = async () => {
      try {
        setLoadingLookups(true);
        const [pResp, partnerResp, ordersRes]: [
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
        ] = await Promise.all([
          getAllProducts(),
          getAllPartners(),
          getProcessingOrders(),
        ]);
        [pResp, partnerResp, ordersRes].forEach(ensureSuccess);

        const pr: Product[] = pResp?.data?.result ?? [];
        const pa: Partner[] = partnerResp?.data?.result ?? [];
        const ord: ProcessingOrderDetails[] = ordersRes?.data?.result ?? [];

        setAllProducts(
          pr.filter((item) => item.type === ProductType.RawMaterial),
        );
        setAllPartners(pa);
        setOrders(ord);
      } catch (err) {
        console.error(err);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju šifarnika i naloga"),
          life: 3000,
        });
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookupsAndOrders();
  }, []);

  useEffect(() => {
    if (id) {
      setProcessing(true);
      setIsPdfReady(false);
      getOneProcessingLog(id)
        .then((response: AxiosResponse) => {
          const newLog = response.data.result;
          if (newLog) {
            if (newLog.date) {
              newLog.date = new Date(newLog.date);
            }
            newLog.usefulWasteKg = Utility.normalizeAmount(
              newLog.usefulWasteKg,
            ) as any;
            newLog.uselessWasteKg = Utility.normalizeAmount(
              newLog.uselessWasteKg,
            ) as any;
            newLog.redGrainKg = Utility.normalizeAmount(
              newLog.redGrainKg,
            ) as any;
            newLog.producedAmountKg = Utility.normalizeAmount(
              newLog.producedAmountKg,
            ) as any;
            newLog.naturalUsedKg = Utility.normalizeAmount(
              newLog.naturalUsedKg,
            ) as any;
            if (Array.isArray(newLog.consumptions)) {
              newLog.consumptions = newLog.consumptions.map((c: any) => ({
                ...c,
                amount: Utility.normalizeAmount(c.amount) as any,
              }));
            }
            setNewProcessingLog(newLog);
            setUpdating(true);
            setIsPdfReady(true);
          }
        })
        .catch((error: AxiosError) => {
          console.log(error);
        })
        .finally(() => {
          setProcessing(false);
        });
    } else {
      setIsPdfReady(true);
      setProcessing(false);
    }
  }, [id]);

  useEffect(() => {
    const generatePdf = () => {
      if (!isPdfReady) return;
      const newPdf = getProcessingLogTemplate(
        newProcessingLog,
        selectedOrder as ProcessingOrderDetails,
      );
      setPdfData(newPdf);
    };

    const timer = setTimeout(generatePdf, 100);
    return () => clearTimeout(timer);
  }, [newProcessingLog, selectedOrder, isPdfReady]);

  const toast = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  //da li je dozvoljen unos loma i crvenog zrna u zavisnosti od karegorije naturalne robe
  const getNaturalSeedPrefix = (
    productName?: string,
  ): "p" | "u" | "su" | "so" | "k" | "j" | "t" | null => {
    const name = (productName ?? "").trim().toLowerCase();

    if (name.startsWith("seme su")) return "su";
    if (name.startsWith("seme so")) return "so";
    if (name.startsWith("seme p")) return "p";
    if (name.startsWith("seme u")) return "u";
    if (name.startsWith("seme k")) return "k";
    if (name.startsWith("seme j")) return "j";
    if (name.startsWith("seme t")) return "t";

    return null;
  };

  //@ts-ignore
  const naturalProductName = selectedOrder?.request?.product?.name ?? "";
  const naturalSeedPrefix = getNaturalSeedPrefix(naturalProductName);

  const isRedGrainEnabled =
    naturalSeedPrefix === "p" ||
    naturalSeedPrefix === "k" ||
    naturalSeedPrefix === "j" ||
    naturalSeedPrefix === "t";

  const isUsefulWasteEnabled =
    naturalSeedPrefix === "so" ||
    naturalSeedPrefix === "p" ||
    naturalSeedPrefix === "k" ||
    naturalSeedPrefix === "j" ||
    naturalSeedPrefix === "t";

  const onSubmit = () => {
    const consumptionsPayload =
      newProcessingLog.consumptions?.map((c) => ({
        logId: newProcessingLog.id ?? "",
        productId: c.productId,
        uom: c.uom,
        amount: Number(c.amount ?? 0),
        isForeign: !!c.isForeign,
        partnerId: c.isForeign ? c.partnerId : undefined,
      })) ?? [];

    const payload = {
      id: newProcessingLog.id,
      number: newProcessingLog.number,
      date: newProcessingLog.date
        ? Utility.toMiddayDate(newProcessingLog.date)
        : Utility.toMiddayDate(new Date()),
      orderId: newProcessingLog.orderId,
      requestId: newProcessingLog.requestId,
      producedProductId: newProcessingLog.producedProductId,
      producedAmountKg:
        newProcessingLog.producedAmountKg != null
          ? Number(newProcessingLog.producedAmountKg)
          : undefined,
      naturalUsedKg:
        newProcessingLog.naturalUsedKg != null
          ? Number(newProcessingLog.naturalUsedKg)
          : undefined,
      usefulWasteKg: newProcessingLog.usefulWasteKg
        ? Number(newProcessingLog.usefulWasteKg)
        : undefined,
      uselessWasteKg: newProcessingLog.uselessWasteKg
        ? Number(newProcessingLog.uselessWasteKg)
        : undefined,
      redGrainKg: newProcessingLog.redGrainKg
        ? Number(newProcessingLog.redGrainKg)
        : undefined,
      closed: newProcessingLog.closed,
      note: newProcessingLog.note,
      consumptions: consumptionsPayload,
    };
    if (updating) {
      updateOneProcessingLog(id as string, payload)
        .then((response: AxiosResponse) => {
          ensureSuccess(response);
          // @ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno izmenjen zapisnik o finalnoj doradi semena`),
            life: 3000,
          });
          navigate(`/processing-logs`);
        })
        .catch((error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            t("Greška prilikom čuvanja zapisnika o finalnoj doradi semena");

          // @ts-ignore
          toast.current.show({
            severity: "error",
            detail: message,
            life: 4000,
          });
        });
    } else {
      addNewProcessingLog(payload)
        .then((response: AxiosResponse) => {
          ensureSuccess(response);
          // @ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno dodat zapisnik o finalnoj doradi semena`),
            life: 3000,
          });
          navigate(`/processing-logs`);
        })
        .catch((error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            t("Greška prilikom čuvanja zapisnika o finalnoj doradi semena");

          // @ts-ignore
          toast.current.show({
            severity: "error",
            detail: message,
            life: 4000,
          });
        });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setNewProcessingLog({
      ...newProcessingLog,
      orderId: "",
      orderNumber: "",
      requestId: "",
      request: {} as ProcessingRequestDetails,
      requestNumber: "",
      order: {} as ProcessingOrderDetails,
      number: "",
      date: new Date(),
      usefulWasteKg: undefined,
      uselessWasteKg: undefined,
      redGrainKg: undefined,
      closed: false,
      producedProductId: undefined,
      producedProduct: undefined,
      producedAmountKg: undefined,
      naturalUsedKg: undefined,
      consumptions: [],
    });
  };

  const handleNewProcessingLog = (prop: string, value: any) => {
    setNewProcessingLog((newProcessingLog) => ({
      ...newProcessingLog,
      [prop]: value,
    }));
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProcesingLogs = cloneDeep(newProcessingLog);
    const newProductFounded = allProducts.find((item) => item.id == value);
    copyOfNewProcesingLogs.producedProduct = newProductFounded;
    copyOfNewProcesingLogs.producedProductId = newProductFounded?.id;
    setNewProcessingLog(copyOfNewProcesingLogs);
  };

  const handleOrderChange = async (_prop: string, value: any) => {
    const foundedOrder = orders.find((item) => item.id === value) as
      | ProcessingOrderDetails
      | undefined;

    try {
      const res = await getOperatorOrderConsumptions(value);

      if (res.data?.status !== "ERROR" && res.data?.status !== "error") {
        const consumptions = res.data.result ?? [];
        setNewProcessingLog((prev) => ({
          ...prev,
          orderId: value,
          order: foundedOrder,
          request: foundedOrder?.request ?? prev.request,
          requestId: foundedOrder?.request?.id ?? prev.requestId,
          consumptions: consumptions,
        }));
      }
    } catch (e) {
      console.log("Greška pri učitavanju repromaterijala:", e);
      console.log("Response data:", (e as any)?.response?.data);
    }
  };

  const handleConsumptionChange = (prop: string, value: any) => {
    setCurrentConsumption((prev) => {
      const updated: any = { ...prev };

      if (prop === "productId") {
        const prod = allProducts.find((p) => p.id === value);
        updated.productId = value;
        updated.productName = prod?.name ?? "";
        updated.uom = prod?.measuringUnit ?? "";
      } else if (prop === "partnerId") {
        updated.partnerId = value || undefined;
        const partner = allPartners.find((p) => p.id === value);
        updated.partnerName = partner?.name ?? "";
      } else if (prop === "amount") {
        updated.amount = value === "" ? ("" as any) : Number(value);
      } else if (prop === "isForeign") {
        updated.isForeign = !!value;
        if (!value) {
          updated.partnerId = undefined;
          updated.partnerName = "";
        }
      } else {
        updated[prop] = value;
      }

      return updated;
    });
  };

  const resetCurrentConsumption = () => {
    setCurrentConsumption({
      productId: "",
      productName: "",
      uom: "",
      amount: 0,
      isForeign: false,
      partnerId: undefined,
      partnerName: "",
    });
    setEditingConsumptionIndex(null);
  };

  const addOrUpdateConsumption = () => {
    if (!currentConsumption.productId || !currentConsumption.amount) {
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Moraju biti popunjeni proizvod i količina"),
        life: 3000,
      });
      return;
    }

    setNewProcessingLog((prev) => {
      const copy = cloneDeep(prev);
      const list = Array.isArray(copy.consumptions)
        ? [...copy.consumptions]
        : [];

      const baseItem: ProcessingLogConsumption = {
        id: currentConsumption.id ?? "",
        logId: copy.id ?? "",
        productId: currentConsumption.productId,
        productName: currentConsumption.productName,
        uom: currentConsumption.uom,
        amount: Number(currentConsumption.amount),
        isForeign: !!currentConsumption.isForeign,
        partnerId: currentConsumption.isForeign
          ? currentConsumption.partnerId
          : undefined,
        partnerName: currentConsumption.isForeign
          ? currentConsumption.partnerName
          : "",
      };

      if (editingConsumptionIndex === null) {
        list.push(baseItem);
      } else {
        list[editingConsumptionIndex] = baseItem;
      }

      copy.consumptions = list;
      return copy;
    });

    resetCurrentConsumption();
  };

  const editConsumption = (
    rowData: ProcessingLogConsumption,
    index: number,
  ) => {
    setCurrentConsumption({
      productId: rowData.productId,
      productName: rowData.productName,
      uom: rowData.uom,
      amount: rowData.amount,
      isForeign: !!rowData.isForeign,
      partnerId: rowData.partnerId,
      partnerName: rowData.partnerName,
      id: rowData.id,
    });
    setEditingConsumptionIndex(index);
  };

  const deleteConsumption = (index: number) => {
    setNewProcessingLog((prev) => {
      const copy = cloneDeep(prev);
      copy.consumptions = (copy.consumptions ?? []).filter(
        (_: any, i: number) => i !== index,
      );
      return copy;
    });
    resetCurrentConsumption();
  };

  const foreignBodyTemplate = (rowData: ProcessingLogConsumption) =>
    rowData.isForeign ? t("DA") : t("NE");

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-file header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Zapisnik o finalnoj doradi semena broj")}: `}
              {newProcessingLog.number ?? ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Svi zapisnici")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/processing-logs`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (newProcessingLog.number) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti zapisnik koji nije snimljen.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (newProcessingLog.number) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati zapisnik koji nije snimljen.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              <TextInput
                id="number"
                value={newProcessingLog.number ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Broj zapisnika")}`}
                placeholderText={t("Broj se kreira automatski pri snimanju")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DateInput
                id="date"
                value={
                  newProcessingLog?.date
                    ? (newProcessingLog.date as Date)
                    : new Date()
                }
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="order"
                value={newProcessingLog.orderId}
                onValueChange={(_, v) => handleOrderChange("orderId", v)}
                optionValue="id"
                optionLabel="number"
                options={orders}
                filter={true}
                labelText={`${t("Nalog za doradu i pakovanje")} *`}
                placeholderText={t("Izaberite nalog za doradu i pakovanje")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInput
                id="plantType"
                value={selectedOrder?.plantType ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Biljna vrsta iz naloga")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={true}
              />
              <TextInput
                id="sort"
                value={selectedOrder?.sort?.name ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Sorta iz naloga")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="seedCategory"
                value={selectedOrder?.seedCategory ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Kategorija semena iz naloga")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={true}
              />
              <TextInput
                id="totalAmountKg"
                value={
                  selectedOrder?.totalAmountKg != null
                    ? Math.round(selectedOrder.totalAmountKg).toString()
                    : ""
                }
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Količina semenske robe iz naloga (kg)")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={true}
              />
              <TextInput
                id="totalAmountKg" //@ts-ignore
                value={selectedOrder?.request?.product?.name ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Naturalna roba iz naloga (kg)")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={true}
              />
              <TextInput
                id="naturalUsedKg"
                value={newProcessingLog?.naturalUsedKg ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Količina potrošene naturalne robe (kg) *")}`}
                placeholderText={t("Uneste potrošenu količinu naturalne robe")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: t("Količina mora biti ceo broj"),
                  },
                }}
              />
              <DropdownInput
                id="producedProduct"
                value={newProcessingLog.producedProduct?.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={allProducts.filter(
                  (oneProd: Product) =>
                    oneProd.category?.name === "SAAT - Seme",
                )}
                filter={true}
                labelText={t("Vrsta proizvedenog semena *")}
                placeholderText={
                  loadingLookups
                    ? t("Učitavanje repromaterijala...")
                    : t("Izaberite vrstu semena")
                }
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={loadingLookups}
              />
              <TextInput
                id="producedAmountKg"
                value={newProcessingLog?.producedAmountKg ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Proizvedena količina semena (kg) *")}`}
                placeholderText={t("Uneste proizvedenu količinu semena")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: t("Količina mora biti ceo broj"),
                  },
                }}
              />
              <TextInput
                id="usefulWasteKg"
                value={newProcessingLog?.usefulWasteKg ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Koristan otpad (kg)")}`}
                placeholderText={t("Uneste količinu korisnog otpada")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: t("Količina mora biti ceo broj"),
                  },
                }}
                disabled={!isUsefulWasteEnabled}
              />
              <TextInput
                id="uselessWasteKg"
                value={newProcessingLog?.uselessWasteKg ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Nekoristan otpad (kg)")}`}
                placeholderText={t("Uneste količinu nekorisnog otpada")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: t("Količina mora biti ceo broj"),
                  },
                }}
              />
              <TextInput
                id="redGrainKg"
                value={newProcessingLog?.redGrainKg ?? ""}
                onValueChange={handleNewProcessingLog}
                labelText={`${t("Crveno zrno (kg)")}`}
                placeholderText={t("Uneste količinu crvenog zrna")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: t("Količina mora biti ceo broj"),
                  },
                }}
                disabled={!isRedGrainEnabled}
              />
              <div className="divider-line">
                <Divider />
              </div>
              <DropdownInput
                id="consumptionProduct"
                value={currentConsumption.productId}
                onValueChange={(_, v) =>
                  handleConsumptionChange("productId", v)
                }
                optionValue="id"
                optionLabel="name"
                options={allProducts}
                filter={true}
                labelText={t("Repromaterijal")}
                placeholderText={
                  loadingLookups
                    ? t("Učitavanje repromaterijala...")
                    : t("Izaberite repromaterijal")
                }
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={loadingLookups}
              />
              <div className="consumption-amount-group">
                <TextInput
                  id="consumptionAmount"
                  value={
                    currentConsumption.amount === 0 ||
                    currentConsumption.amount === ("" as any)
                      ? ""
                      : (currentConsumption.amount?.toString() ?? "")
                  }
                  onValueChange={(_, v) => handleConsumptionChange("amount", v)}
                  labelText={t("Količina")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
                <TextInput
                  id="consumptionUom"
                  value={currentConsumption.uom ?? ""}
                  onValueChange={() => {}}
                  labelText={t("Jedinica mere")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              </div>
              <div className="consumption-owner-group">
                <RadioButtonInput
                  id="isForeign"
                  value={currentConsumption.isForeign}
                  radioButtonValue1={t("DA")}
                  radioButtonValue2={t("NE")}
                  onValueChange={(_, v) =>
                    handleConsumptionChange("isForeign", v)
                  }
                  labelText={t("Tuđa roba")}
                  control={control}
                  errors={errors}
                />
                <div className="owner-field">
                  {currentConsumption.isForeign && (
                    <DropdownInput
                      id="consumptionPartner"
                      value={currentConsumption.partnerId}
                      onValueChange={(_, v) =>
                        handleConsumptionChange("partnerId", v)
                      }
                      optionValue="id"
                      optionLabel="name"
                      options={allPartners}
                      filter={true}
                      labelText={t("Vlasnik robe")}
                      placeholderText={t("Izaberite partnera")}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                    />
                  )}
                </div>
              </div>
              <div className="add-consumption-button">
                <ButtonInput
                  labelText={
                    editingConsumptionIndex === null
                      ? t("Dodaj repromaterijal")
                      : t("Sačuvaj stavku")
                  }
                  icon="bi bi-plus-square"
                  onButtonClick={addOrUpdateConsumption}
                  addStyle={{
                    border: "none",
                    background: "rgba(21, 115, 71, 1)",
                    color: "#ffffff",
                    marginTop: "0.5rem",
                  }}
                />
              </div>
              <div className="data-table-container">
                <DataTable
                  value={newProcessingLog.consumptions ?? []}
                  responsiveLayout="scroll"
                >
                  <Column
                    field="productName"
                    header={t("Naziv repromaterijala")}
                  />
                  <Column field="uom" header={t("M.J.")} />
                  <Column field="amount" header={t("Količina")} />
                  <Column body={foreignBodyTemplate} header={t("Tuđa roba")} />
                  <Column field="partnerName" header={t("Vlasnik robe")} />
                  <Column
                    header={t("Opcije")}
                    body={(rowData, options) => (
                      <div className="repro-actions">
                        <ButtonInput
                          labelText=""
                          icon="bi bi-pencil"
                          onButtonClick={() =>
                            editConsumption(
                              rowData as ProcessingLogConsumption,
                              options.rowIndex,
                            )
                          }
                          addStyle={{
                            backgroundColor: "white",
                            color: "rgba(21, 115, 71, 1)",
                            border: "none",
                          }}
                        />
                        <ButtonInput
                          labelText=""
                          icon="bi bi-trash"
                          onButtonClick={() =>
                            deleteConsumption(options.rowIndex)
                          }
                          addStyle={{
                            backgroundColor: "white",
                            color: "rgba(220, 53, 69, 1)",
                            border: "none",
                            marginLeft: ".25rem",
                          }}
                        />
                      </div>
                    )}
                    className="datatable-button-column"
                  />
                </DataTable>
              </div>
              <RadioButtonInput
                id="closed"
                value={newProcessingLog.closed}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleNewProcessingLog}
                labelText={t("Zaključen zapisnik")}
                control={control}
                errors={errors}
              />
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
            <div className="documents-preview">
              {pdfData ? (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`${t("Zapisnik o finalnoj doradi semena")}_${
                    newProcessingLog?.number
                  }.pdf`}
                />
              ) : (
                <p>Učitavam PDF prikaz...</p>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
